import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import { pensionProviderIdOptions } from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Option } from '@nxsys/types/models/Common';
import { Form } from 'antd';
import { FormProps } from 'antd/lib';
import React from 'react';
import { useEmployerFieldLabels } from '@nxsys/hooks/useEmployerFieldLabels';

type AutoEnrollmentFilterModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onFilter: (params: AutoEnrollmentFilterValues) => void;
};

export type AutoEnrollmentFilterValues = {
  companies: Option<string>[];
  pensionProvider: Option<string> | null;
};

const AutoEnrollmentFilterModal: React.FC<AutoEnrollmentFilterModalProps> = ({
  isOpen,
  toggle,
  onFilter,
}: AutoEnrollmentFilterModalProps) => {
  const { messages } = useTypedIntl();
  const employerLabels = useEmployerFieldLabels();

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const ableToClearFilter =
    watchValue && Object.keys(watchValue).some((key) => !!watchValue[key]);

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    onFilter(values);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={messages['common.filter']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!ableToClearFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Form.Item label={employerLabels.label} name="companies">
          <AppSelectSync
            type="company"
            apiType="searchCompany"
            mode="multiple"
            placeholder={messages['form.pleaseSelect']}
            onChangeValue={(value) => {
              form.setFieldsValue({
                companies: value,
              });
            }}
          />
        </Form.Item>

        <Form.Item
          label={messages['common.pensionProvider']}
          name="pensionProvider"
        >
          <AppSelect
            options={pensionProviderIdOptions}
            placeholder={messages['form.select']}
            onChange={(value) => {
              form.setFieldsValue({
                pensionProvider: value,
              });
            }}
          />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default AutoEnrollmentFilterModal;
